<script setup lang="ts">
import { computed, inject, onMounted, ref, useTemplateRef, watchEffect } from "vue";
import { useRoute } from "vue-router";

import SideDrawer from "@/components/ui/SideDrawer.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type ProjectDto, type WorkspaceDto } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { useApi } from "@/services/api";
import { useModalsStore } from "@/stores/modals";
import { useToastsStore } from "@/stores/toasts";
import ProjectsListViewCard from "@/views/projects/ProjectsListViewCard.vue";
import PutReportTutorial from "@/views/PutReportTutorial.vue";

const api = useApi();
const route = useRoute();
const modals = useModalsStore();
const toasts = useToastsStore();

const raiseError = inject(raiseErrorKey);

const isLoading = ref(true);
const projects = ref<ProjectDto[]>([]);
const cardFirstSectionWidth = ref(-1);
const workspace = ref<WorkspaceDto | null>(null);

const tutorialDrawer = useTemplateRef<InstanceType<typeof SideDrawer>>("tutorialDrawer");

const workspacePublicId = computed(() => route.params.workspacePublicId as string);

async function refreshProjects() {
  isLoading.value = true;
  const results = await api.listProjects(workspacePublicId.value);
  if (results.length >= 0) {
    projects.value = results;
  } else {
    raiseError?.(404);
  }
  isLoading.value = false;
}

async function deleteProject(projectName: string) {
  if (
    await modals.confirm({
      title: "Delete project",
      message: `Do you really want to delete project ${projectName}?`,
    })
  ) {
    const success = await api.deleteProject(workspacePublicId.value, projectName);
    if (success) {
      toasts.addToast(`Project ${projectName} deleted`, "success", {
        duration: 5,
      });
    }
  }
  await refreshProjects();
}

function openTutorialDrawer() {
  tutorialDrawer.value?.open();
}

watchEffect(async () => {
  workspace.value = await api.fetchWorkspaceByPublicId(workspacePublicId.value);
});

onMounted(async () => {
  await refreshProjects();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: workspace?.name ?? workspacePublicId,
        icon: 'icon-masonry',
      },
    ]"
  >
    <div class="projects-view">
      <div v-if="isLoading" class="loading">
        <SkoreSpinner />
      </div>

      <div v-else class="wrapper">
        <header>
          <div class="text-2xl-medium">Projects</div>
          <div class="subtitle text-base-regular">Browse the projects you created here.</div>
        </header>

        <div v-if="projects.length > 0" class="projects" ref="cards">
          <ProjectsListViewCard
            v-for="project in projects"
            :key="project.id"
            :project="project"
            :workspace-public-id="workspacePublicId"
            :first-section-width="cardFirstSectionWidth"
            @delete="deleteProject(project.name)"
          />
        </div>

        <div v-else class="placeholder">
          <header>
            <div class="icon">
              <i class="icon icon-scan-cube" />
            </div>

            <div class="text-xl-medium">No projects yet</div>

            <div class="subtitle text-base-regular">
              Projects appear here when you create them using the skore library in your code.
              <br />
              Check the
              <a href="https://docs.skore.probabl.ai/" target="_blank">documentation</a> to get
              started.
            </div>
          </header>

          <div class="actions">
            <SimpleButton
              label="See how you could easily add a project"
              icon-right="icon-ascending-arrow"
              @click="openTutorialDrawer"
            />
          </div>
        </div>
      </div>
    </div>
  </DefaultPageLayout>

  <Teleport to="body">
    <SideDrawer ref="tutorialDrawer" content-width="60dvw">
      <template #title>Get started using a local IDE or notebook</template>

      <PutReportTutorial :workspace-public-id="workspacePublicId" />
    </SideDrawer>
  </Teleport>
</template>

<style scoped>
.projects-view {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  padding-top: var(--numbers-32);
  gap: var(--numbers-32);

  .loading {
    margin-top: 45dvh;
    text-align: center;
  }

  .wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--numbers-32);

    header {
      display: flex;
      flex-direction: column;
      gap: var(--numbers-4);

      .subtitle {
        color: var(--content-tertiary);
      }
    }

    .projects {
      display: grid;
      gap: var(--numbers-16);
      grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
    }

    .placeholder {
      display: flex;
      flex: 1;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-bottom: var(--numbers-32);
      gap: var(--numbers-32);
      text-align: center;

      header {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--numbers-16);

        .subtitle {
          color: var(--content-secondary);
        }
      }

      .icon {
        margin-bottom: var(--numbers-12);

        i {
          padding: var(--numbers-8);
          border-radius: var(--numbers-6);
          background-color: var(--background-tertiary);
          color: var(--content-brand);
          vertical-align: middle;
        }
      }
    }
  }
}
</style>
